import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
    UpdateLocationConflict,
    useUpdateStockLocationConfiguration,
} from "../../api/stocklocations";
import { StockLocation } from "../../shared/types";
import { stockLocationDisplayName } from "../../shared/stock-location-display-name";

type EditConfigurationDialogProps = {
    open: boolean;
    location: StockLocation;
    onClose: () => void;
    onSuccess: () => void;
};

type Step = "edit" | "conflict";

export default function EditConfigurationDialog({
    open,
    location,
    onClose,
    onSuccess,
}: EditConfigurationDialogProps) {
    const { updateConfiguration, submitting } = useUpdateStockLocationConfiguration();
    const [step, setStep] = useState<Step>("edit");
    const [levels, setLevels] = useState(String(location.levels));
    const [columns, setColumns] = useState(String(location.columns ?? 1));
    const [error, setError] = useState<string | null>(null);
    const [conflict, setConflict] = useState<UpdateLocationConflict | null>(null);

    useEffect(() => {
        if (!open) return;
        setStep("edit");
        setLevels(String(location.levels));
        setColumns(String(location.columns ?? 1));
        setError(null);
        setConflict(null);
    }, [open, location]);

    const levelsNum = parseInt(levels, 10);
    const columnsNum = parseInt(columns, 10);
    const levelsValid = Number.isFinite(levelsNum) && levelsNum >= 1;
    const columnsValid = Number.isFinite(columnsNum) && columnsNum >= 1;
    const unchanged =
        levelsValid &&
        columnsValid &&
        levelsNum === location.levels &&
        columnsNum === (location.columns ?? 1);

    async function handleSave() {
        if (!levelsValid || !columnsValid || unchanged) return;
        setError(null);

        const result = await updateConfiguration(location.id, levelsNum, columnsNum);
        if (result.ok) {
            onSuccess();
            onClose();
            return;
        }

        if (result.conflict) {
            setConflict(result.conflict);
            setStep("conflict");
            return;
        }

        setError(result.error ?? "Failed to update location configuration.");
    }

    async function handleRelocate() {
        if (!levelsValid || !columnsValid) return;
        setError(null);

        const result = await updateConfiguration(location.id, levelsNum, columnsNum, "relocate");
        if (result.ok) {
            onSuccess();
            onClose();
            return;
        }

        setError(result.error ?? "Failed to update location configuration.");
        setStep("edit");
    }

    function handleCancelConflict() {
        setConflict(null);
        setStep("edit");
    }

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
            {step === "edit" ? (
                <>
                    <DialogTitle>Edit configuration</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Update the number of levels and columns for{" "}
                                <strong>{location.name}</strong>.
                            </Typography>

                            {error && <Alert severity="error">{error}</Alert>}

                            <Typography variant="caption" color="text.secondary">Levels</Typography>
                            <TextField
                                placeholder="Levels"
                                type="number"
                                value={levels}
                                onChange={(e) => setLevels(e.target.value)}
                                inputProps={{ min: 1, step: 1 }}
                                error={levels !== "" && !levelsValid}
                                helperText={
                                    levels !== "" && !levelsValid
                                        ? "Enter a whole number of at least 1"
                                        : " "
                                }
                                fullWidth
                                autoFocus
                            />

                            <Typography variant="caption" color="text.secondary">Columns</Typography>
                            <TextField
                                placeholder="Columns"
                                type="number"
                                value={columns}
                                onChange={(e) => setColumns(e.target.value)}
                                inputProps={{ min: 1, step: 1 }}
                                error={columns !== "" && !columnsValid}
                                helperText={
                                    columns !== "" && !columnsValid
                                        ? "Enter a whole number of at least 1"
                                        : " "
                                }
                                fullWidth
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClose} disabled={submitting}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={submitting || !levelsValid || !columnsValid || unchanged}
                            startIcon={submitting ? <CircularProgress size={16} /> : undefined}
                        >
                            Save
                        </Button>
                    </DialogActions>
                </>
            ) : (
                <>
                    <DialogTitle>Stock outside new configuration</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2}>
                            <DialogContentText>
                                {conflict?.affected_count ?? 0} stock item
                                {(conflict?.affected_count ?? 0) === 1 ? "" : "s"}{" "}
                                {(conflict?.affected_count ?? 0) === 1 ? "is" : "are"} currently on a
                                level or column that would no longer be accessible.
                            </DialogContentText>

                            {error && <Alert severity="error">{error}</Alert>}

                            {(conflict?.affected_stock?.length ?? 0) > 0 && (
                                <Box
                                    sx={{
                                        maxHeight: 220,
                                        overflow: "auto",
                                        border: 1,
                                        borderColor: "divider",
                                        borderRadius: 1,
                                        p: 1.5,
                                    }}
                                >
                                    <Stack spacing={1}>
                                        {conflict!.affected_stock.slice(0, 20).map((item) => (
                                            <Typography key={item.id} variant="body2">
                                                <strong>{item.product?.code ?? "—"}</strong>
                                                {" · "}
                                                {item.product?.name ?? "Unknown product"}
                                                {" · "}
                                                {stockLocationDisplayName(item.location, item.level, item.column)}
                                            </Typography>
                                        ))}
                                        {(conflict!.affected_count ?? 0) > 20 && (
                                            <Typography variant="caption" color="text.secondary">
                                                And {(conflict!.affected_count ?? 0) - 20} more…
                                            </Typography>
                                        )}
                                    </Stack>
                                </Box>
                            )}

                            <DialogContentText>
                                Choose <strong>Relocate</strong> to create into-stock relocation
                                requests for these items and apply the new configuration, or{" "}
                                <strong>Cancel</strong> to keep the current layout.
                            </DialogContentText>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelConflict} disabled={submitting}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="warning"
                            onClick={handleRelocate}
                            disabled={submitting}
                            startIcon={submitting ? <CircularProgress size={16} /> : undefined}
                        >
                            Relocate
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
}
